import { Locale } from "@/Common/Models/Localization/Locale";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { BaseBritishColumbiaObstetricalFileSubSection, BaseBritishColumbiaObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/BritishColumbia/Subcomponent/BaseBritishColumbiaObstetricalFileSubSection";
import { ObstetricalFileFieldOptions } from "@/Patient/Components/MedicalNote/Obstetric/Form/Common/Subcomponent/BaseObstetricalFileSubSection";
import { GestationalAgeComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/GestationalAgeComponent";
import { DateSelectorRenderOptions } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRendererOptions";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";

enum ContraceptiveTypeLabel {
    None = "NoneWordLabel",
    OralContraceptive = "ContraceptiveType_OralContraceptiveAbbrev",
    Ring = "ContraceptiveType_Ring",
    Patch = "ContraceptiveType_Patch",
    Condom = "ContraceptiveType_Condom",
    IntraUterineDevice = "ContraceptiveType_IntraUterineDeviceAbbrev",
    Rhythm = "ContraceptiveType_Rhythm",
    Injection = "ContraceptiveType_Injection"
}

class BritishColumbiaObstetricalFileSection3 extends BaseBritishColumbiaObstetricalFileSubSection<BaseBritishColumbiaObstetricalFileSubSectionProps, never> {
    private readonly daysInCycle: number = 28;

    public render(): JSX.Element {
        const section3 = this.props.form.Section3;

        return (
            <div className="section border border-bottom">
                <div className="row flex-row padding-sm-top padding-sm-bottom">
                    {this.renderField({
                        label: "ContraceptivesTypeLabel",
                        field: this.controlRenderer.renderAutoCompleteTextbox({
                            value: section3.ContraceptiveType,
                            autoCompleteItems: this.getContraceptiveTypeOptions(),
                            disableMatchAutoSorting: true,
                            onChange: text => this.props.mutateModelState(s => s.Section3.ContraceptiveType = text),
                            onAutoCompleteSelected: (code, _, value) => this.props.mutateModelState(s => s.Section3.ContraceptiveType = value),
                            maxLength: this.page1FieldLimits.ContraceptivesType.MaxChars
                        }),
                        labelPrefix: this.getTitleNumberAsLabelPrefix(3)
                    })}
                    {this.renderField({
                        label: "LastUsedLabel",
                        field: this.renderDateSelector({
                            value: section3.ContraceptiveLastUsedDate,
                            onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section3.ContraceptiveLastUsedDate = value)
                        })
                    })}
                    {this.renderField({
                        label: "PregnancyPlannedLabel",
                        field: this.controlRenderer.renderYesNoRadio(
                            section3.PregnancyPlanned,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section3.PregnancyPlanned = value),
                            "PregnancyPlanned",
                            { paddingTop: "7px" }
                        ),
                        className: "nowrap"
                    })}
                    {this.renderField({
                        label: "ObstetricalFile_LastMenstrualPeriodAbbreviationLabel",
                        field: this.renderDateSelector({
                            value: section3.LastMenstrualPeriod,
                            onChange: value => this.props.mutateModelStateWithFormUpdate(
                                s => {
                                    s.Section3.LastMenstrualPeriod = value;
                                    s.Section3.EstimatedDateOfBirthByLastMenstrualPeriod = ObstetricsCalculator.calculateDateOfBirthBasedOnLastMenstrualPeriod(value, this.daysInCycle);
                                    this.setAgeAtEstimatedDateOfBirth(s);
                                    s.updateSubsequentVisitGestationalAge(this.props.contributionId);
                                }
                            )
                        })
                    })}
                    {this.renderField({
                        label: "ObstetricalFile_EddByLmp",
                        field: this.renderDateSelector({
                            value: section3.EstimatedDateOfBirthByLastMenstrualPeriod,
                            onChange: value => this.props.mutateModelStateWithFormUpdate(
                                s => {
                                    s.Section3.EstimatedDateOfBirthByLastMenstrualPeriod = value;
                                    this.setAgeAtEstimatedDateOfBirth(s);
                                    s.updateSubsequentVisitGestationalAge(this.props.contributionId);
                                }
                            )
                        })
                    })}
                    {this.renderField({
                        label: "DatingUltrasoundAbbrevLabel",
                        field: this.renderDateSelector({
                            value: section3.DatingUltrasound,
                            onChange: value => this.props.mutateModelStateWithFormUpdate(s => {
                                s.Section3.DatingUltrasound = value;
                                this.setEstimatedDateOfBirthByUltrasound(s);
                            })
                        })
                    })}
                    {this.renderField({
                        label: "ObstetricalFile_GestationalAgeByUltrasoundAbbrevLabel",
                        field: <GestationalAgeComponent
                            id="GestationAgeByUS"
                            gestationalAge={section3.GestationalAgeByUltrasound}
                            readonly={this.props.readonly}
                            onChange={value => this.props.mutateModelState(s => {
                                s.Section3.GestationalAgeByUltrasound = value;
                                this.setEstimatedDateOfBirthByUltrasound(s);
                            })}
                            locale={Locale.en}
                            controlRenderer={this.controlRenderer}
                        />
                    })}
                    {this.renderField({
                        label: "ObstetricalFile_EddByUltrasoundAbbrev",
                        field: this.renderDateSelector({
                            value: section3.EstimatedDateOfBirthByUltrasound,
                            onChange: value => this.props.mutateModelStateWithFormUpdate(
                                s => {
                                    s.Section3.EstimatedDateOfBirthByUltrasound = value;
                                    this.setAgeAtEstimatedDateOfBirth(s);
                                    s.updateSubsequentVisitGestationalAge(this.props.contributionId);
                                }
                            )
                        })
                    })}
                </div>
            </div>
        );
    }

    protected renderField(options: ObstetricalFileFieldOptions): JSX.Element {
        return super.renderField({
            label: options.label,
            field: options.field,
            className: `padding-md-left padding-md-right ${options.className ?? ""}`,
            labelPrefix: options.labelPrefix
        });
    }

    private setEstimatedDateOfBirthByUltrasound(state: BritishColumbiaObstetricalFileForm): void {
        const section3 = state.Section3;

        if (section3.GestationalAgeByUltrasound?.hasValue() && section3.DatingUltrasound) {
            state.Section3.EstimatedDateOfBirthByUltrasound = ObstetricsCalculator.calculateDateOfBirthBasedOnUltrasound(section3.DatingUltrasound, section3.GestationalAgeByUltrasound.TotalNumberOfDays);
            this.setAgeAtEstimatedDateOfBirth(state);
        }
    }

    private renderDateSelector(options: DateSelectorRenderOptions): JSX.Element {
        return this.controlRenderer.renderDateSelector({ ...options, className: "width-100" });
    }

    private getContraceptiveTypeOptions(): SearchableItemModelT<string>[] {
        return Object.values(ContraceptiveTypeLabel).map(p => new SearchableItemModelT(this.getString(p), p));
    }

    private setAgeAtEstimatedDateOfBirth(state: BritishColumbiaObstetricalFileForm): void {
        const patientDateOfBirth = this.props.patient.IndividualResponse.DateOfBirth;
        const estimateDateOfBirth = state.Section3.EstimatedDateOfBirthByUltrasound ?? state.Section3.EstimatedDateOfBirthByLastMenstrualPeriod;

        state.updateAgeAtEstimatedDateOfBirth(patientDateOfBirth, estimateDateOfBirth);
    }
}

export { BritishColumbiaObstetricalFileSection3 };
